/**
 * Ước lượng chi phí — APM-AI-o (`plan-ai-features.md` §10.2).
 *
 * Hàm **thuần**, ở một package riêng vì cả hai phía đều cần: giao diện tính lại
 * ngay khi người dùng đổi một ô ước lượng, và API tính khi trả báo cáo. Hai bản
 * cài đặt cho một công thức là hai con số sẽ lệch nhau đúng vào lúc ai đó sửa
 * một bên.
 *
 * ## Mỗi con số đi kèm `confidence` và `missing`
 *
 * Một tổng chi phí không nói nó thiếu gì thì đọc như một con số chắc chắn. Ở đây
 * thiếu cái gì thì nói ra cái đó: task chưa ước lượng, người chưa có đơn giá,
 * loại việc chưa khai giá. Người đọc quyết định nhìn con số ấy nghiêm túc tới
 * đâu — đó là việc của họ, không phải của phép tính.
 */
/**
 * Ranh giới giữa ba mức khó, tính bằng **giờ**.
 *
 * `≤ 4h` là việc làm gọn trong một buổi; `> 16h` là việc kéo quá hai ngày công,
 * tức là thứ phải chia nhỏ hoặc phải suy nghĩ. Khoảng giữa là mặc định.
 *
 * Hằng số chứ chưa phải cấu hình theo dự án: chưa ai cần con số khác, và một ô
 * cấu hình không ai chỉnh vẫn là một ô phải bảo trì. `bucketOf` nhận ngưỡng qua
 * tham số nên mở ra sau này không phải viết lại chỗ nào.
 */
export const DIFFICULTY_THRESHOLDS = { simple: 4, hard: 16 };
/**
 * Việc này thuộc mức khó nào.
 *
 * `null` khi chưa biết giờ — và đó là câu trả lời đúng, không phải `'standard'`:
 * chưa biết giờ thì cũng chưa tính được tiền, nên task ấy vào `missing` chứ
 * không vào tổng kèm một mức khó đoán bừa.
 */
export function bucketOf(hours, thresholds = DIFFICULTY_THRESHOLDS) {
    if (hours === null || hours <= 0)
        return null;
    if (hours <= thresholds.simple)
        return 'simple';
    if (hours > thresholds.hard)
        return 'hard';
    return 'standard';
}
/**
 * Số giờ của một task.
 *
 * Thứ tự rơi: giờ đã ước lượng → điểm story × giờ mỗi điểm. Không có cả hai thì
 * `null` — **không** đoán một con số mặc định. Một task "8 giờ" bịa ra nhân với
 * bốn mươi task là một tổng sai mà không ai thấy nó sai ở đâu.
 */
export function taskHours(task, rates) {
    if (task.estimateHours !== null && task.estimateHours > 0)
        return task.estimateHours;
    if (task.storyPoints !== null && task.storyPoints > 0 && rates.hoursPerStoryPoint !== null) {
        return task.storyPoints * rates.hoursPerStoryPoint;
    }
    return null;
}
/**
 * Đơn giá áp cho một task: người → chức vụ → kỹ năng.
 *
 * Thứ tự ấy là thứ tự **cụ thể dần xuống**, và nó có lý do nghiệp vụ: tổ chức
 * nào nhập lương từng người thì con số chính xác nhất nằm ở đó; tổ chức nào
 * không muốn nhập thì khai theo chức vụ vẫn ra một tổng có nghĩa (xem APM-AI-n).
 */
export function taskRate(task, rates) {
    if (task.assigneeId && rates.byUser[task.assigneeId] !== undefined) {
        return rates.byUser[task.assigneeId];
    }
    if (task.projectRoleId && rates.byProjectRole[task.projectRoleId] !== undefined) {
        return rates.byProjectRole[task.projectRoleId];
    }
    if (task.skillId && rates.bySkill[task.skillId] !== undefined) {
        return rates.bySkill[task.skillId];
    }
    return null;
}
/**
 * Kẹp hệ số trong khoảng dùng được.
 *
 * Một con số gõ nhầm thành `10` nhân với cả dự án ra một tổng vô nghĩa mà trông
 * vẫn y hệt một tổng đúng. Zod đã chặn ở biên nhập; kẹp lại ở đây vì phép tính
 * cũng chạy trên dữ liệu cũ, và một hàng sai đã nằm sẵn trong bảng thì không có
 * lần validate nào chạy lại trên nó.
 */
export const SPEED_FACTOR_RANGE = { min: 0.25, max: 4 };
/**
 * Hệ số tốc độ áp cho một task: người nhận × mức khó của việc.
 *
 * Trả `1` — **không** phải `null` — khi không tra được. Khác hẳn đơn giá: thiếu
 * giá thì không tính được tiền, còn thiếu hệ số thì "đúng mức chuẩn" là một câu
 * trả lời trung tính đúng nghĩa. Vì thế thiếu hệ số **không** vào `missing`.
 *
 * Task chưa có người nhận cũng là `1`: không đoán theo chức vụ, vì một hệ số là
 * nhận định về một con người cụ thể chứ không về một cái ghế.
 */
export function taskSpeedFactor(task, rates, hours) {
    const bucket = bucketOf(hours);
    if (bucket === null || task.assigneeId === null)
        return 1;
    const factor = rates.speedByUser[task.assigneeId]?.[bucket];
    if (factor === undefined || factor <= 0)
        return 1;
    return Math.min(SPEED_FACTOR_RANGE.max, Math.max(SPEED_FACTOR_RANGE.min, factor));
}
export function taskCost(task, rates, items) {
    const hours = taskHours(task, rates);
    const rate = taskRate(task, rates);
    const attached = items
        .filter((item) => item.taskId === task.taskId)
        .reduce((sum, item) => sum + item.amount, 0);
    // Giờ ước lượng là giờ của một người **middle chuẩn**; hệ số quy nó về người
    // thật sẽ làm. Nếu người ước lượng đã tự tính "giao cho Nam thì nhanh hơn" thì
    // phép nhân này đếm hai lần — nên nghĩa ấy được nói ở nhãn ô nhập giờ, ở
    // glossary và ở docblock của `CostTask.estimateHours`.
    const speedFactor = taskSpeedFactor(task, rates, hours);
    const effectiveHours = hours === null ? null : round2(hours * speedFactor);
    const labour = effectiveHours !== null && rate !== null ? round2(effectiveHours * rate) : null;
    return {
        taskId: task.taskId,
        hours,
        difficulty: bucketOf(hours),
        speedFactor,
        effectiveHours,
        rate,
        labour,
        items: round2(attached),
        total: labour === null ? null : round2(labour + attached),
        // Giờ suy từ điểm story kém chắc hơn giờ do người ước lượng — nói ra chứ
        // đừng trộn hai loại vào một con số trông giống nhau.
        confidence: labour === null
            ? 'low'
            : task.estimateHours !== null && task.estimateHours > 0
                ? 'high'
                : 'medium',
    };
}
/**
 * Một khoản định kỳ còn phải trả **bao nhiêu lần** trong `daysRemaining` ngày.
 *
 * - `daily` / `weekly` / `monthly`: làm tròn số kỳ (`monthly` = 30 ngày — đúng
 *   phép tính trước 2026-09-30, khi đầu vào còn là `monthsRemaining`, nên mọi
 *   khoản hàng tháng đang có ra y nguyên con số cũ).
 * - `yearly`: làm tròn **lên**. Một khoản năm (bản quyền, tên miền) không chia
 *   nhỏ được: còn 5 tháng là vẫn phải trả trọn một năm, và làm tròn xuống ra 0
 *   là để một hoá đơn thật biến mất khỏi dự báo.
 * - `once`: luôn 1.
 */
export function occurrencesLeft(recurrence, daysRemaining) {
    const days = Math.max(0, daysRemaining);
    switch (recurrence) {
        case 'once':
            return 1;
        case 'daily':
            return Math.round(days);
        case 'weekly':
            return Math.round(days / 7);
        case 'monthly':
            return Math.round(days / 30);
        case 'yearly':
            return Math.ceil(days / 365);
    }
}
export function projectCost(input) {
    const missing = [];
    let labour = 0;
    let covered = 0;
    let speedAdjusted = 0;
    for (const task of input.tasks) {
        const cost = taskCost(task, input.rates, input.items);
        if (cost.hours === null)
            missing.push({ kind: 'task_hours', taskId: task.taskId });
        if (cost.rate === null)
            missing.push({ kind: 'rate', taskId: task.taskId });
        if (cost.labour === null)
            continue;
        labour += cost.labour;
        covered += 1;
        if (cost.speedFactor !== 1)
            speedAdjusted += 1;
    }
    // Khoản chi **không gắn task** cộng vào tổng dự án; khoản định kỳ nhân theo
    // số kỳ còn lại (`occurrencesLeft`). Khoản gắn task đã nằm trong `taskCost` —
    // cộng lần nữa ở đây là tính hai lần, và tổng sẽ luôn cao hơn sự thật.
    const projectItems = input.items
        .filter((item) => item.taskId === null)
        .reduce((sum, item) => sum + item.amount * occurrencesLeft(item.recurrence, input.daysRemaining), 0);
    const attachedItems = input.items
        .filter((item) => item.taskId !== null)
        .reduce((sum, item) => sum + item.amount, 0);
    const items = round2(projectItems + attachedItems);
    const base = labour + items;
    const contingency = round2((base * Math.max(0, input.contingencyPct)) / 100);
    return {
        labour: round2(labour),
        items,
        contingency,
        total: round2(base + contingency),
        confidence: confidenceOf(covered, input.tasks.length),
        missing,
        covered,
        taskCount: input.tasks.length,
        speedAdjusted,
    };
}
/**
 * Độ tin cậy của tổng, theo **tỉ lệ task tính được**.
 *
 * Không do ai tự phong: một tổng dựng từ ba phần tư số task thì nó là một tổng
 * của ba phần tư dự án, bất kể phần tính được chính xác tới đâu.
 */
function confidenceOf(covered, total) {
    if (total === 0)
        return 'low';
    const ratio = covered / total;
    if (ratio >= 0.9)
        return 'high';
    if (ratio >= 0.6)
        return 'medium';
    return 'low';
}
function round2(value) {
    return Math.round(value * 100) / 100;
}
//# sourceMappingURL=cost.js.map